import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Upload } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

import {
  createLead, createFacilityLead, listLeads,
  updateLeadStatus, updateLeadBilling, createContract, createTask,
  uploadDocument, uploadStaffDocument, createInternalNote,
  SERVICES, URGENCY, CONTRACT_STATUSES, TASK_ASSIGNEES,
  DOCUMENT_CATEGORIES, STAFF_DOC_GROUPS, INTERNAL_NOTE_CATEGORIES,
} from "@/lib/api";

// Normalize FastAPI error responses (422 detail is an array of error objects, not a string)
// so we never pass raw objects to toast.error → React-child crash.
const errorMessage = (err, fallback = "Something went wrong.") => {
  const detail = err?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        if (typeof d === "string") return d;
        if (d && typeof d === "object") {
          const loc = Array.isArray(d.loc) ? d.loc.filter((p) => p !== "body").join(".") : "";
          return loc ? `${loc}: ${d.msg || "invalid"}` : (d.msg || "invalid");
        }
        return "invalid";
      })
      .join("; ");
  }
  if (typeof detail === "object") return detail.msg || JSON.stringify(detail);
  return fallback;
};

// Lead sources captured by the quick-action (prepended to notes for searchability
// since the public form doesn't have this field).
const LEAD_SOURCES = [
  "Phone Call",
  "Email",
  "Referral",
  "Networking Event",
  "Walk-In",
  "Partnership",
  "Manual Entry — Other",
];

const Shell = ({ open, onClose, title, description, footer, children, testid, wide = false }) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
    <DialogContent
      className={(wide ? "sm:max-w-2xl" : "sm:max-w-lg") + " max-h-[90vh] overflow-y-auto"}
      data-testid={testid}
    >
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        {description && <DialogDescription>{description}</DialogDescription>}
      </DialogHeader>
      <div className="grid gap-4 py-2">{children}</div>
      <DialogFooter>{footer}</DialogFooter>
    </DialogContent>
  </Dialog>
);

// =====================================================================
// 1. Lead (also used by Client + Service Request)
// =====================================================================
export const QuickLeadDialog = ({ open, mode = "lead", onClose, onSaved }) => {
  const isClient = mode === "client";
  const isServiceRequest = mode === "service_request";
  const titleByMode = {
    lead: "Add new lead",
    client: "Add new client",
    service_request: "Add new service request",
  };
  const descByMode = {
    lead: "Capture an inbound opportunity (phone, email, referral, walk-in) — it'll flow into the Leads pipeline.",
    client: "Add an existing or recurring customer — same workflow as a lead with an extra onboarding note.",
    service_request: "Create a service request on behalf of a customer — same workflow as a lead.",
  };

  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);

  function empty() {
    return {
      full_name: "",
      organization: "",
      phone: "",
      email: "",
      services: [],
      service_address: "",
      preferred_date: "",
      preferred_time: "",
      urgency: "Standard",
      source: "Phone Call",
      notes: "",
    };
  }
  useEffect(() => { if (open) setForm(empty()); }, [open, mode]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const toggleService = (label) => {
    setForm((s) => ({
      ...s,
      services: s.services.includes(label) ? s.services.filter((x) => x !== label) : [...s.services, label],
    }));
  };

  const save = async () => {
    if (!form.full_name.trim()) { toast.error("Lead name is required."); return; }
    if (!form.email.trim()) { toast.error("Email is required."); return; }
    if (!form.phone.trim()) { toast.error("Phone is required."); return; }
    if (form.services.length === 0) { toast.error("Pick at least one service."); return; }
    setSaving(true);
    try {
      const notesParts = [`Source: ${form.source}`];
      if (isClient) notesParts.push("Onboarded directly as active client.");
      if (isServiceRequest) notesParts.push("Created via Quick Actions — Service Request.");
      if (form.notes.trim()) notesParts.push(form.notes.trim());

      const payload = {
        full_name: form.full_name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        company: form.organization.trim() || null,
        services: form.services,
        service_address: form.service_address.trim() || null,
        preferred_date: form.preferred_date || null,
        preferred_time: form.preferred_time || null,
        urgency: form.urgency,
        notes: notesParts.join("\n\n"),
      };
      const lead = await createLead(payload);
      // For "Add Client" mode, auto-set status to scheduled (active client)
      if (isClient && lead?.id) {
        try { await updateLeadStatus(lead.id, "scheduled"); } catch {}
      }
      toast.success(`${titleByMode[mode]} — saved. Lead ID: ${lead.id.slice(0, 8)}…`);
      onSaved?.(lead);
      onClose?.();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to save."));
    } finally { setSaving(false); }
  };

  return (
    <Shell
      open={open}
      onClose={onClose}
      title={titleByMode[mode]}
      description={descByMode[mode]}
      testid="quick-lead-dialog"
      wide
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} style={{ background: "#0B2545", color: "#FFFFFF" }} data-testid="quick-lead-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Save {mode === "lead" ? "lead" : mode === "client" ? "client" : "request"}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <Label>Lead / Contact name *</Label>
          <Input value={form.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="Jane Doe" data-testid="qlead-name" />
        </div>
        <div>
          <Label>Organization (optional)</Label>
          <Input value={form.organization} onChange={(e) => set("organization", e.target.value)} placeholder="Acme Clinic" data-testid="qlead-org" />
        </div>
        <div>
          <Label>Phone *</Label>
          <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="555-123-4567" data-testid="qlead-phone" />
        </div>
        <div className="sm:col-span-2">
          <Label>Email *</Label>
          <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="jane@acme.com" data-testid="qlead-email" />
        </div>
        <div className="sm:col-span-2">
          <Label>Service interested in *</Label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 mt-1.5">
            {SERVICES.map((s) => (
              <label key={s.id} className="flex items-start gap-2 text-sm rounded border px-2.5 py-2 cursor-pointer"
                     style={{ borderColor: form.services.includes(s.label) ? "#0B2545" : "#E2E8F0",
                              background: form.services.includes(s.label) ? "rgba(11,37,69,0.03)" : "#FFFFFF" }}
                     data-testid={`qlead-service-${s.id}`}>
                <Checkbox className="mt-0.5" checked={form.services.includes(s.label)} onCheckedChange={() => toggleService(s.label)} />
                <span style={{ color: "#0B2545" }}>{s.label}</span>
              </label>
            ))}
          </div>
        </div>
        <div>
          <Label>Lead source</Label>
          <Select value={form.source} onValueChange={(v) => set("source", v)}>
            <SelectTrigger data-testid="qlead-source"><SelectValue /></SelectTrigger>
            <SelectContent>
              {LEAD_SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Priority</Label>
          <Select value={form.urgency} onValueChange={(v) => set("urgency", v)}>
            <SelectTrigger data-testid="qlead-priority"><SelectValue /></SelectTrigger>
            <SelectContent>
              {URGENCY.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Preferred date (optional)</Label>
          <Input type="date" value={form.preferred_date} onChange={(e) => set("preferred_date", e.target.value)} data-testid="qlead-date" />
        </div>
        <div>
          <Label>Preferred time (optional)</Label>
          <Input value={form.preferred_time} onChange={(e) => set("preferred_time", e.target.value)} placeholder="9:00 AM" data-testid="qlead-time" />
        </div>
        <div className="sm:col-span-2">
          <Label>Service address (optional)</Label>
          <Input value={form.service_address} onChange={(e) => set("service_address", e.target.value)} placeholder="123 Main St, Sandusky, OH" data-testid="qlead-address" />
        </div>
        <div className="sm:col-span-2">
          <Label>Notes</Label>
          <Textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="qlead-notes" placeholder="Anything else…" />
        </div>
      </div>
    </Shell>
  );
};

// =====================================================================
// 2. Facility / Provider
// =====================================================================
const FACILITY_SERVICES = [
  "Staffing Support", "Multi-Patient Collections", "Recurring Visits", "Medical Courier", "Other",
];
const FACILITY_COVERAGES = ["One-Time", "Ongoing", "Urgent"];

export const QuickFacilityDialog = ({ open, onClose, onSaved }) => {
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);

  function empty() {
    return {
      facility_name: "", contact_person: "", title_role: "", email: "", phone: "",
      address: "", service_needed: "Staffing Support", coverage_needed: "Ongoing",
      volume_staffing_needs: "", preferred_start_date: "", notes: "",
    };
  }
  useEffect(() => { if (open) setForm(empty()); }, [open]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!form.facility_name.trim()) { toast.error("Facility name is required."); return; }
    if (!form.contact_person.trim()) { toast.error("Contact name is required."); return; }
    if (!form.email.trim() || !form.phone.trim()) { toast.error("Email and phone are required."); return; }
    setSaving(true);
    try {
      await createFacilityLead({
        facility_name: form.facility_name.trim(),
        contact_person: form.contact_person.trim(),
        title_role: form.title_role.trim() || null,
        email: form.email.trim(),
        phone: form.phone.trim(),
        address: form.address.trim() || null,
        service_needed: form.service_needed,
        coverage_needed: form.coverage_needed,
        volume_staffing_needs: form.volume_staffing_needs.trim() || null,
        preferred_start_date: form.preferred_start_date || null,
        notes: form.notes.trim() || null,
      });
      toast.success("Facility / Provider added.");
      onSaved?.(); onClose?.();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to save."));
    } finally { setSaving(false); }
  };

  return (
    <Shell open={open} onClose={onClose} testid="quick-facility-dialog" wide
      title="Add facility / provider"
      description="Add a B2B facility or healthcare provider partner."
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} style={{ background: "#0B2545", color: "#FFFFFF" }} data-testid="quick-facility-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Save
          </Button>
        </>
      }>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label>Facility name *</Label><Input value={form.facility_name} onChange={(e) => set("facility_name", e.target.value)} data-testid="qfac-name" /></div>
        <div><Label>Contact name *</Label><Input value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} data-testid="qfac-contact" /></div>
        <div><Label>Role / title</Label><Input value={form.title_role} onChange={(e) => set("title_role", e.target.value)} data-testid="qfac-role" /></div>
        <div><Label>Phone *</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="qfac-phone" /></div>
        <div className="sm:col-span-2"><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="qfac-email" /></div>
        <div className="sm:col-span-2"><Label>Address</Label><Input value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="qfac-address" /></div>
        <div>
          <Label>Service needed *</Label>
          <Select value={form.service_needed} onValueChange={(v) => set("service_needed", v)}>
            <SelectTrigger data-testid="qfac-service"><SelectValue /></SelectTrigger>
            <SelectContent>{FACILITY_SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Coverage *</Label>
          <Select value={form.coverage_needed} onValueChange={(v) => set("coverage_needed", v)}>
            <SelectTrigger data-testid="qfac-coverage"><SelectValue /></SelectTrigger>
            <SelectContent>{FACILITY_COVERAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Preferred start date</Label><Input type="date" value={form.preferred_start_date} onChange={(e) => set("preferred_start_date", e.target.value)} data-testid="qfac-start" /></div>
        <div className="sm:col-span-2"><Label>Volume / staffing needs</Label><Textarea rows={2} value={form.volume_staffing_needs} onChange={(e) => set("volume_staffing_needs", e.target.value)} data-testid="qfac-volume" /></div>
        <div className="sm:col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="qfac-notes" /></div>
      </div>
    </Shell>
  );
};

// =====================================================================
// 3. Contract (lite — title + status + value + dates + optional PDF)
// =====================================================================
export const QuickContractDialog = ({ open, onClose, onSaved, link }) => {
  const [form, setForm] = useState(empty());
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  function empty() {
    return { title: link?.label ? `${link.label} — Service Agreement` : "", status: "draft", start_date: "", end_date: "", value: "", notes: "" };
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) { setForm(empty()); setFile(null); } }, [open]);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required."); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title.trim());
      fd.append("status", form.status);
      if (form.start_date) fd.append("start_date", form.start_date);
      if (form.end_date) fd.append("end_date", form.end_date);
      if (form.value) fd.append("value", form.value);
      if (form.notes.trim()) fd.append("notes", form.notes.trim());
      if (file) fd.append("file", file);
      if (link?.type) {
        fd.append("link_type", link.type);
        fd.append("link_id", link.id);
        fd.append("link_label", link.label || "");
      }
      await createContract(fd);
      toast.success("Contract created.");
      onSaved?.(); onClose?.();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to save."));
    } finally { setSaving(false); }
  };

  return (
    <Shell open={open} onClose={onClose} testid="quick-contract-dialog"
      title="Add contract"
      description={link?.label ? `Linked to ${link.label}. Visit the Contracts panel for the full editor.` : "Quick contract record. Visit the Contracts panel for the full editor (links, more fields)."}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} style={{ background: "#0B2545", color: "#FFFFFF" }} data-testid="quick-contract-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Create
          </Button>
        </>
      }>
      <div><Label>Title *</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="qcontract-title" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Status</Label>
          <Select value={form.status} onValueChange={(v) => set("status", v)}>
            <SelectTrigger data-testid="qcontract-status"><SelectValue /></SelectTrigger>
            <SelectContent>{CONTRACT_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Value (USD)</Label><Input type="number" min="0" step="0.01" value={form.value} onChange={(e) => set("value", e.target.value)} data-testid="qcontract-value" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Start</Label><Input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} data-testid="qcontract-start" /></div>
        <div><Label>End</Label><Input type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} data-testid="qcontract-end" /></div>
      </div>
      <div><Label>Attach PDF (optional, max 25 MB)</Label><Input type="file" accept=".pdf,.docx,.doc,.jpg,.jpeg,.png" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="qcontract-file" />
        {file && <p className="text-xs mt-1.5" style={{ color: "#64748B" }}>{file.name} · {Math.round(file.size/1024)} KB</p>}
      </div>
      <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="qcontract-notes" /></div>
    </Shell>
  );
};

// =====================================================================
// Helper: lead picker (for invoice/payment/refund)
// =====================================================================
const useLeadPicker = (open) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try { const data = await listLeads(); if (!cancelled) setLeads(data || []); }
      catch { /* non-fatal */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open]);
  return { leads, loading };
};

const LeadPicker = ({ leads, loading, value, onChange, testid }) => {
  if (!loading && (!leads || leads.length === 0)) {
    return (
      <div className="rounded-lg border-2 border-dashed p-4 text-center"
           style={{ borderColor: "#FECACA", color: "#9B1C1C" }} data-testid={`${testid}-empty`}>
        <p className="text-sm font-medium">No leads available</p>
        <p className="text-xs mt-1" style={{ color: "#64748B" }}>
          Add a lead first via the &quot;Add Lead&quot; quick action.
        </p>
      </div>
    );
  }
  return (
    <div>
      <Label>Lead *</Label>
      <Select value={value || ""} onValueChange={onChange} disabled={loading}>
        <SelectTrigger data-testid={testid}><SelectValue placeholder={loading ? "Loading…" : "Pick a lead"} /></SelectTrigger>
        <SelectContent>
          {leads.slice(0, 100).map((l) => (
            <SelectItem key={l.id} value={l.id}>
              {l.full_name} · {l.email}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

// =====================================================================
// 4. Invoice — set amount_due + pricing_type + payment_link on a lead
// =====================================================================
export const QuickInvoiceDialog = ({ open, onClose, onSaved }) => {
  const { leads, loading } = useLeadPicker(open);
  const [leadId, setLeadId] = useState("");
  const [amount, setAmount] = useState("");
  const [pricingType, setPricingType] = useState("Standard");
  const [stripeLink, setStripeLink] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setLeadId(""); setAmount(""); setPricingType("Standard"); setStripeLink(""); } }, [open]);

  const save = async () => {
    if (!leadId) { toast.error("Pick a lead."); return; }
    if (!amount || Number(amount) <= 0) { toast.error("Amount must be > 0."); return; }
    setSaving(true);
    try {
      await updateLeadBilling(leadId, {
        amount_due: Number(amount),
        pricing_type: pricingType,
        stripe_payment_link: stripeLink.trim() || null,
        payment_status: "payment_link_sent",
      });
      toast.success("Invoice recorded on lead.");
      onSaved?.(); onClose?.();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to save."));
    } finally { setSaving(false); }
  };

  return (
    <Shell open={open} onClose={onClose} testid="quick-invoice-dialog"
      title="Add invoice"
      description="Record the amount due on an existing lead and (optionally) the Stripe payment link."
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} style={{ background: "#0B2545", color: "#FFFFFF" }} data-testid="quick-invoice-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Save invoice
          </Button>
        </>
      }>
      <LeadPicker leads={leads} loading={loading} value={leadId} onChange={setLeadId} testid="qinvoice-lead" />
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Amount due *</Label><Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="qinvoice-amount" /></div>
        <div>
          <Label>Pricing type</Label>
          <Select value={pricingType} onValueChange={setPricingType}>
            <SelectTrigger data-testid="qinvoice-pricing"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Standard">Standard</SelectItem>
              <SelectItem value="Rush">Rush</SelectItem>
              <SelectItem value="Custom Payment">Custom Payment</SelectItem>
              <SelectItem value="Facility / Provider">Facility / Provider</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div><Label>Stripe payment link (optional)</Label><Input value={stripeLink} onChange={(e) => setStripeLink(e.target.value)} placeholder="https://buy.stripe.com/…" data-testid="qinvoice-link" /></div>
    </Shell>
  );
};

// =====================================================================
// 5. Payment — record amount_paid on a lead
// =====================================================================
export const QuickPaymentDialog = ({ open, onClose, onSaved }) => {
  const { leads, loading } = useLeadPicker(open);
  const [leadId, setLeadId] = useState("");
  const [amount, setAmount] = useState("");
  const [status, setStatus] = useState("paid");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setLeadId(""); setAmount(""); setStatus("paid"); } }, [open]);

  const save = async () => {
    if (!leadId) { toast.error("Pick a lead."); return; }
    if (!amount || Number(amount) <= 0) { toast.error("Amount must be > 0."); return; }
    setSaving(true);
    try {
      await updateLeadBilling(leadId, {
        amount_paid: Number(amount),
        payment_status: status,
      });
      toast.success("Payment recorded.");
      onSaved?.(); onClose?.();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to save."));
    } finally { setSaving(false); }
  };

  return (
    <Shell open={open} onClose={onClose} testid="quick-payment-dialog"
      title="Record payment"
      description="Mark a payment received on an existing lead."
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} style={{ background: "#0B2545", color: "#FFFFFF" }} data-testid="quick-payment-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Record payment
          </Button>
        </>
      }>
      <LeadPicker leads={leads} loading={loading} value={leadId} onChange={setLeadId} testid="qpayment-lead" />
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Amount paid *</Label><Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="qpayment-amount" /></div>
        <div>
          <Label>Status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="qpayment-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="paid">Paid in full</SelectItem>
              <SelectItem value="partial_payment">Partial payment</SelectItem>
              <SelectItem value="remaining_balance">Remaining balance</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </Shell>
  );
};

// =====================================================================
// 6. Refund Request — flag a lead as cancelled (refund pending)
// =====================================================================
export const QuickRefundDialog = ({ open, onClose, onSaved }) => {
  const { leads, loading } = useLeadPicker(open);
  const [leadId, setLeadId] = useState("");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setLeadId(""); setAmount(""); setReason(""); } }, [open]);

  const save = async () => {
    if (!leadId) { toast.error("Pick a lead."); return; }
    if (!reason.trim()) { toast.error("Reason is required for audit."); return; }
    setSaving(true);
    try {
      const body = {
        payment_status: "cancelled",
      };
      if (amount) body.amount_paid = Number(amount); // record refund amount
      await updateLeadBilling(leadId, body);
      // Create a task to track follow-up
      try {
        await createTask({
          title: `Refund request: $${amount || "TBD"}`,
          assignee: "shared",
          link_type: "lead",
          link_id: leadId,
          link_label: leads.find((l) => l.id === leadId)?.full_name || "Lead",
          notes: `Refund reason: ${reason.trim()}`,
        });
      } catch {}
      toast.success("Refund request logged — task created.");
      onSaved?.(); onClose?.();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to save."));
    } finally { setSaving(false); }
  };

  return (
    <Shell open={open} onClose={onClose} testid="quick-refund-dialog"
      title="Create refund request"
      description="Mark a lead's payment as cancelled and create a follow-up task with the reason on file."
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} style={{ background: "#9B1C1C", color: "#FFFFFF" }} data-testid="quick-refund-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Log refund
          </Button>
        </>
      }>
      <LeadPicker leads={leads} loading={loading} value={leadId} onChange={setLeadId} testid="qrefund-lead" />
      <div><Label>Refund amount (optional)</Label><Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="qrefund-amount" /></div>
      <div><Label>Reason *</Label><Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is this refund being issued?" data-testid="qrefund-reason" /></div>
    </Shell>
  );
};

// =====================================================================
// 7. Document upload (client / business / financial)
// =====================================================================
export const QuickDocumentDialog = ({ open, onClose, onSaved }) => {
  const [file, setFile] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [category, setCategory] = useState("business");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setFile(null); setDisplayName(""); setCategory("business"); setNotes(""); } }, [open]);

  const save = async () => {
    if (!file) { toast.error("Choose a file."); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", category);
      if (displayName.trim()) fd.append("display_name", displayName.trim());
      if (notes.trim()) fd.append("notes", notes.trim());
      await uploadDocument(fd);
      toast.success("Document uploaded.");
      onSaved?.(); onClose?.();
    } catch (err) {
      toast.error(errorMessage(err, "Upload failed."));
    } finally { setSaving(false); }
  };

  return (
    <Shell open={open} onClose={onClose} testid="quick-document-dialog"
      title="Upload document"
      description="Client, business, or financial record. Up to 25 MB."
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} style={{ background: "#0B2545", color: "#FFFFFF" }} data-testid="quick-document-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
            Upload
          </Button>
        </>
      }>
      <div><Label>File *</Label><Input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="qdoc-file" />
        {file && <p className="text-xs mt-1.5" style={{ color: "#64748B" }}>{file.name} · {Math.round(file.size/1024)} KB</p>}
      </div>
      <div><Label>Display name</Label><Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} data-testid="qdoc-name" /></div>
      <div>
        <Label>Category</Label>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="qdoc-category"><SelectValue /></SelectTrigger>
          <SelectContent>{DOCUMENT_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div><Label>Notes</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="qdoc-notes" /></div>
    </Shell>
  );
};

// =====================================================================
// 8. Staff credential upload
// =====================================================================
export const QuickStaffCredentialDialog = ({ open, onClose, onSaved }) => {
  const [file, setFile] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [personName, setPersonName] = useState("");
  const [group, setGroup] = useState("employee_staff");
  const [subcategory, setSubcategory] = useState("certifications");
  const [expirationDate, setExpirationDate] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const subcatOptions = useMemo(
    () => STAFF_DOC_GROUPS.find((g) => g.value === group)?.subcategories || [],
    [group],
  );

  useEffect(() => {
    if (!open) return;
    setFile(null); setDisplayName(""); setPersonName("");
    setGroup("employee_staff"); setSubcategory("certifications");
    setExpirationDate(""); setNotes("");
  }, [open]);

  useEffect(() => { if (!subcatOptions.find((s) => s.value === subcategory)) setSubcategory(subcatOptions[0]?.value || ""); }, [group]); // eslint-disable-line

  const save = async () => {
    if (!file) { toast.error("Choose a file."); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("group", group);
      fd.append("subcategory", subcategory);
      if (displayName.trim()) fd.append("display_name", displayName.trim());
      if (personName.trim()) fd.append("person_name", personName.trim());
      if (notes.trim()) fd.append("notes", notes.trim());
      if (expirationDate) fd.append("expiration_date", expirationDate);
      await uploadStaffDocument(fd);
      toast.success("Staff credential uploaded.");
      onSaved?.(); onClose?.();
    } catch (err) {
      toast.error(errorMessage(err, "Upload failed."));
    } finally { setSaving(false); }
  };

  return (
    <Shell open={open} onClose={onClose} testid="quick-staff-dialog"
      title="Upload staff credential"
      description="CPR/BLS, license, certification, or any staff document."
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} style={{ background: "#0B2545", color: "#FFFFFF" }} data-testid="quick-staff-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
            Upload
          </Button>
        </>
      }>
      <div><Label>File *</Label><Input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="qstaff-file" />
        {file && <p className="text-xs mt-1.5" style={{ color: "#64748B" }}>{file.name} · {Math.round(file.size/1024)} KB</p>}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Person name</Label><Input value={personName} onChange={(e) => setPersonName(e.target.value)} placeholder="e.g. Jane Doe" data-testid="qstaff-person" /></div>
        <div><Label>Display name</Label><Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} data-testid="qstaff-displayname" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Group</Label>
          <Select value={group} onValueChange={setGroup}>
            <SelectTrigger data-testid="qstaff-group"><SelectValue /></SelectTrigger>
            <SelectContent>{STAFF_DOC_GROUPS.map((g) => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Subcategory</Label>
          <Select value={subcategory} onValueChange={setSubcategory}>
            <SelectTrigger data-testid="qstaff-sub"><SelectValue /></SelectTrigger>
            <SelectContent>{subcatOptions.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div><Label>Expiration date</Label><Input type="date" value={expirationDate} onChange={(e) => setExpirationDate(e.target.value)} data-testid="qstaff-expiry" /></div>
      <div><Label>Notes</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="qstaff-notes" /></div>
    </Shell>
  );
};

// =====================================================================
// 9. Task (lightweight reuse — POST /tasks)
// =====================================================================
export const QuickTaskDialog = ({ open, onClose, onSaved }) => {
  const [title, setTitle] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [assignee, setAssignee] = useState("shared");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setTitle(""); setDueDate(""); setAssignee("shared"); setNotes(""); } }, [open]);

  const save = async () => {
    if (!title.trim()) { toast.error("Title required."); return; }
    setSaving(true);
    try {
      await createTask({ title: title.trim(), due_date: dueDate || null, assignee, notes: notes || null });
      toast.success("Task created.");
      onSaved?.(); onClose?.();
    } catch (err) {
      toast.error(errorMessage(err, "Failed."));
    } finally { setSaving(false); }
  };

  return (
    <Shell open={open} onClose={onClose} testid="quick-task-dialog"
      title="Create task"
      description="Personal or shared reminder."
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} style={{ background: "#0B2545", color: "#FFFFFF" }} data-testid="quick-task-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Create
          </Button>
        </>
      }>
      <div><Label>Title *</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="qtask-title" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Due date</Label><Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="qtask-due" /></div>
        <div>
          <Label>Assignee</Label>
          <Select value={assignee} onValueChange={setAssignee}>
            <SelectTrigger data-testid="qtask-assignee"><SelectValue /></SelectTrigger>
            <SelectContent>{TASK_ASSIGNEES.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div><Label>Notes</Label><Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="qtask-notes" /></div>
    </Shell>
  );
};

// =====================================================================
// 10. Internal Note (lightweight reuse — POST /internal-notes)
// =====================================================================
export const QuickInternalNoteDialog = ({ open, onClose, onSaved }) => {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState("business_ideas");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setTitle(""); setBody(""); setCategory("business_ideas"); } }, [open]);

  const save = async () => {
    if (!title.trim()) { toast.error("Title required."); return; }
    setSaving(true);
    try {
      await createInternalNote({ title: title.trim(), body: body || null, category });
      toast.success("Note saved.");
      onSaved?.(); onClose?.();
    } catch (err) {
      toast.error(errorMessage(err, "Failed."));
    } finally { setSaving(false); }
  };

  return (
    <Shell open={open} onClose={onClose} testid="quick-note-dialog"
      title="Add internal note"
      description="Idea, plan, vendor note — searchable from the Knowledge Base."
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} style={{ background: "#0B2545", color: "#FFFFFF" }} data-testid="quick-note-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Save
          </Button>
        </>
      }>
      <div><Label>Title *</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="qnote-title" /></div>
      <div>
        <Label>Category</Label>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="qnote-category"><SelectValue /></SelectTrigger>
          <SelectContent>{INTERNAL_NOTE_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div><Label>Notes</Label><Textarea rows={5} value={body} onChange={(e) => setBody(e.target.value)} data-testid="qnote-body" /></div>
    </Shell>
  );
};
